import { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useGame } from './net/GameProvider';
import { Backdrop } from './components/Backdrop';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { LeaveRoomButton } from './components/LeaveRoomButton';
import { Banner, Page, Row } from './components/ui';
import { HomeScreen } from './screens/HomeScreen';
import { LobbyScreen } from './screens/LobbyScreen';
import { WritingScreen } from './screens/WritingScreen';
import { QuestionScreen } from './screens/QuestionScreen';
import { RevealScreen } from './screens/RevealScreen';
import { GameOverScreen } from './screens/GameOverScreen';

const TopBar = styled(Row)`
  justify-content: flex-end;
`;

const ERROR_DISMISS_MS = 4500;

export function App() {
  const { t } = useTranslation();
  const { state, status, error, clearError, pendingCode } = useGame();

  // Errors are transient nudges, not modal blockers — they fade on their own.
  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(clearError, ERROR_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [error, clearError]);

  return (
    <>
      <Backdrop />
      <Page>
        <TopBar>
          {/* Shown in a room, and also while holding an unaccepted invite —
              otherwise a share link has no way back out. */}
          {(state !== null || pendingCode !== null) && <LeaveRoomButton />}
          <LanguageSwitcher />
        </TopBar>

        {status !== 'open' && (
          <Banner $tone="info">
            {status === 'connecting' ? `🫧 ${t('common.reconnecting')}` : `📡 ${t('common.offline')}`}
          </Banner>
        )}

        {error && <Banner $tone="error">{t(`errors.${error}`)}</Banner>}

        {state === null && <HomeScreen />}
        {state?.phase === 'lobby' && <LobbyScreen state={state} />}
        {state?.phase === 'writing' && <WritingScreen state={state} />}
        {state?.phase === 'question' && <QuestionScreen state={state} />}
        {state?.phase === 'reveal' && <RevealScreen state={state} />}
        {state?.phase === 'gameover' && <GameOverScreen state={state} />}
      </Page>
    </>
  );
}
